import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { TrialBanner, ImpersonationBanner } from "@/components/TrialBanner";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, Briefcase, UserPlus, Gavel, Calendar, FileText, Settings, Scale, Timer, LogOut, ShieldCheck, Search, Landmark, FolderOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NotificationsBell } from "@/components/NotificationsBell";
import { useRecordTime } from "@/components/RecordTimeModal";
import { GlobalSearch } from "@/components/GlobalSearch";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/bits";

const NAV = [
  { name: "Dashboard", icon: LayoutDashboard, path: "/", roles: ["admin", "lawyer"] },
  { name: "Clients", icon: Users, path: "/clients" },
  { name: "Matters", icon: Briefcase, path: "/matters" },
  { name: "Leads", icon: UserPlus, path: "/leads" },
  { name: "Court Log", icon: Gavel, path: "/court-log" },
  { name: "Schedule", icon: Calendar, path: "/schedule" },
  { name: "Invoices", icon: FileText, path: "/invoices", roles: ["admin", "lawyer"] },
  { name: "Trust", icon: Landmark, path: "/trust", roles: ["admin", "lawyer"] },
  { name: "Documents", icon: FolderOpen, path: "/documents", roles: ["admin"] },
  { name: "Workload", icon: Users, path: "/workload", roles: ["admin"] },
  { name: "Settings", icon: Settings, path: "/settings", roles: ["admin"] },
];

export function brandVars(firm) {
  const c = firm?.brand_color || "#2DD4BF";
  const n = parseInt(c.slice(1), 16);
  const lighten = (v) => Math.min(255, Math.round(v + (255 - v) * 0.25));
  const hover = `#${[lighten(n >> 16), lighten((n >> 8) & 255), lighten(n & 255)].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
  return { "--brand": c, "--brand-hover": hover };
}

export default function Layout() {
  const { user, firm, logout } = useAuth();
  const { openRecordTime } = useRecordTime();
  const nav = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((o) => !o); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="min-h-screen flex" style={brandVars(firm)}>
      <aside className="w-64 fixed inset-y-0 left-0 border-r border-[#1C2541] flex flex-col z-40" style={{ background: firm?.sidebar_color || "#080E21" }} data-testid="sidebar">
        <div className="px-5 h-16 flex items-center gap-3 border-b border-[#1C2541]" data-testid="firm-brand">
          {firm?.logo_url ? <img src={firm.logo_url} alt="" className="w-9 h-9 rounded-md object-contain bg-white/5 border border-[color-mix(in_srgb,var(--brand)_30%,transparent)]" data-testid="firm-logo" />
            : <div className="w-9 h-9 rounded-md bg-[color-mix(in_srgb,var(--brand)_15%,transparent)] border border-[color-mix(in_srgb,var(--brand)_40%,transparent)] flex items-center justify-center font-serif text-lg gold-text" data-testid="firm-logo">{(firm?.name || "F")[0]}</div>}
          <div className="min-w-0">
            <p className="font-serif text-base text-white leading-tight line-clamp-2" data-testid="firm-name" title={firm?.name}>{firm?.name || "Workspace"}</p>
            <p className="text-[10px] text-slate-500 tracking-widest uppercase mt-0.5">Practice workspace</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map((n) => (
            <NavLink key={n.path} to={n.path} end={n.path === "/"} data-testid={`sidebar-link-${n.name.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${isActive ? "bg-[color-mix(in_srgb,var(--brand)_10%,transparent)] text-[var(--brand-hover)] border-l-2 border-[var(--brand)]" : "text-slate-400 hover:text-white hover:bg-[#131C38]"}`}>
              <n.icon className="w-4 h-4" /> {n.name}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1C2541]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1C2541] flex items-center justify-center font-serif text-[var(--brand)]">{user.name?.[0]}</div>
            <div className="min-w-0">
              <p className="text-sm text-white truncate" data-testid="sidebar-user-name">{user.name}</p>
              <Pill value={user.role} testId="sidebar-user-role" />
            </div>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-button" className="mt-4 w-full flex items-center gap-2 text-xs text-slate-400 hover:text-red-300 transition-colors"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
          <p className="mt-4 pt-3 border-t border-[#1C2541] flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-slate-600" data-testid="powered-by-jurisos"><Scale className="w-3 h-3 text-[#2DD4BF]" /> Powered by JurisOS</p>
        </div>
      </aside>
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="h-16 sticky top-0 z-30 border-b border-[#1C2541] bg-[#0B132B]/85 backdrop-blur-lg px-8 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-400"><ShieldCheck className="w-4 h-4 text-emerald-400" /> MFA verified session <span className="text-slate-600">·</span> <span className="text-slate-300" data-testid="header-firm-name">{firm?.name}</span>{firm?.status === "trial" && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#2DD4BF]/15 text-[#2DD4BF] text-[10px] font-semibold" data-testid="trial-badge">Trial</span>}</div>
          <div className="flex items-center gap-3">
            <button onClick={() => setSearchOpen(true)} data-testid="header-global-search-button" className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-[#2E3B61] bg-[#0B132B] text-xs text-slate-400 hover:border-[color-mix(in_srgb,var(--brand)_50%,transparent)] transition-colors w-64"><Search className="w-3.5 h-3.5" /> Search anything… <kbd className="ml-auto font-mono text-[10px] border border-[#2E3B61] rounded px-1">⌘K</kbd></button>
            {user.role !== "support" && <Button onClick={() => openRecordTime()} className="btn-gold gap-2" data-testid="header-record-time-button"><Timer className="w-4 h-4" /> Record Time</Button>}
            <NotificationsBell />
          </div>
        </header>
        <ImpersonationBanner />
        <TrialBanner />
        <main className="flex-1 p-8 max-w-[1500px] w-full"><Outlet /></main>
      </div>
      <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
